import React, { useContext, useMemo, useState } from 'react';
import YouTube from 'react-youtube';
import styled from 'styled-components/macro';

import { UserAgent } from '@mirinae/js-utils';

import { getFields } from '@mirinae/lessons/modules/libs/elementLib';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { FormattedKoEnPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';

import ReplaySVG from '@mirinae/lessons/assets/images/videoReplay.svg';
import ReplayHoverSVG from '@mirinae/lessons/assets/images/videoReplayHover.svg';

const isMobile = !UserAgent.os.isOthers;

const Wrapper = styled(Element)`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 100;
`;

const getSeconds = (hours, minutes, seconds) => {
    const [ihours, iminutes, iseconds] = [
        parseInt(hours, 10),
        parseInt(minutes, 10),
        parseInt(seconds, 10),
    ];

    return (ihours * 60 * 60) + (iminutes * 60) + iseconds;
};

const SubtitleAndReplay = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 20px;
    cursor: pointer;
`;

const ReplayButton = styled.div`
    margin: 5px 5px 0 0;
`;

const Subtitle = styled(FormattedKoEnPhrase)`
`;

const SubTitledVideoClip = ({ element }) => {
    const { startHour, startMinute, startSecond, length, url } = getFields(
        element,
        'startHour',
        'startMinute',
        'startSecond',
        'length',
        'url',
        'subtitle',
    );

    const player = useContext(PlayerContext);
    const [video, setVideo] = useState(null);
    const startSeconds = getSeconds(startHour, startMinute, startSecond);
    const endSeconds = useMemo(() => startSeconds + parseInt(length, 10), [
        startHour, 
        startMinute, 
        startSecond,
        length,
    ]);
    const [watched, setWatched] = useState(false);
    const [subtitleHover, setSubtitleHover] = useState(false);

    // YouTube's embed API doesn't provide a way to hook into
    // playtime updates, so you have to do it manually. Here
    // we just use a basic interval to poll offset time with
    // getCurrentTime();
    const [pollInterval, setPollInterval] = useState(null);

    const inputUrl = new URL(url);
    const videoId = inputUrl.searchParams.get('v');

    const complete = () => {
        setWatched(true);
        video.pauseVideo();
        player.postEvent({ action: 'complete' }, element);
    };

    const onReady = (event) => {
        setVideo(event.target);
    };

    const onPlay = () => {
        pollPlayTime();
        const interval = setInterval(() => pollPlayTime(), 100);
        setPollInterval(interval);
    };

    const onStop = () => {
        if (pollInterval != null) {
            clearInterval(pollInterval);
        }
    };

    const pollPlayTime = () => {
        const playbackSeconds = video.getCurrentTime();
        if (playbackSeconds != null && playbackSeconds >= endSeconds && !watched) {
            complete();
        }
    };

    const replay = () => {
        setWatched(false);
        setPollInterval(null);
        video.seekTo(startSeconds);
        video.playVideo();
    };

    const videoOptions = {
        height: isMobile ? 207 : 213, // (element.geomElement || element).geom.height, // 213,
        width: isMobile ? 368 : 380, // (element.geomElement || element).geom.width, // 379,
        playerVars: { start: startSeconds },
    };

    return (
        <Wrapper element={element}>
            <YouTube
                videoId={videoId}
                onReady={onReady}
                onPlay={onPlay}
                onPause={onStop}
                onEnd={onStop}
                opts={videoOptions}
            />
            <SubtitleAndReplay onMouseEnter={() => setSubtitleHover(true)} onMouseLeave={() => setSubtitleHover(false)} onClick={replay}>
                <ReplayButton>
                    { subtitleHover ? <img src={ReplayHoverSVG} alt="Replay" /> : <img src={ReplaySVG} alt="Replay" /> }
                </ReplayButton>
                <Subtitle phrase={element.fieldsByName.subtitle} phraseStyle={{ noTextTools: true, hover: subtitleHover }} />
            </SubtitleAndReplay>
        </Wrapper>
    );
};

export default SubTitledVideoClip;
